import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  updateTranslation,
  getElderVerificationCounts,
  getLanguageVerificationData,
  handleSessionError,
  addNewPhrase,
  checkPhraseExists,
} from './Airtable';
import CustomAudioRecorder from './CustomAudioRecorder';
import { capitalizeFirstLetter } from './utils'; // Import the utility function
import './App.css'; // Ensure you are importing the combined App.css

const NOOP_COUNTS_LOADED = () => {};
const NOOP_COUNTS_ERROR = () => {};
const NON_RETRYABLE_DATA_CODES = ['AIRTABLE_NOT_CONFIGURED', 'LANGUAGE_NOT_ALLOWED'];

const TranslationVerification = ({ elder, language, languageId, languageUuid, onLogout, onSwitchLanguage, setTotalVerified, setTodayVerified, onCountsLoaded = NOOP_COUNTS_LOADED, onCountsError = NOOP_COUNTS_ERROR, readOnly = false }) => {
  const [translations, setTranslations] = useState({ needingVerification: [], needingTranslation: [] });
  const [currentTranslation, setCurrentTranslation] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [editMode, setEditMode] = useState(false); // Track if in edit mode
  const [newTranslation, setNewTranslation] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [languageName, setLanguageName] = useState(''); // Store the actual language name
  const [verifiedTranslations, setVerifiedTranslations] = useState([]); // Store verified translations
  const [newPhrase, setNewPhrase] = useState('');
  const [newPhraseTranslation, setNewPhraseTranslation] = useState('');
  const [showAddPhraseForm, setShowAddPhraseForm] = useState(false); // Track the state of add phrase form
  const [addingPhrase, setAddingPhrase] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [loadWarning, setLoadWarning] = useState('');
  const [canRetry, setCanRetry] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const onLogoutRef = useRef(onLogout);
  const countRequestIdRef = useRef(0);
  const addingPhraseRef = useRef(false);
  const savingRef = useRef(false);

  const handleWriteError = useCallback(async (error) => {
    console.error('Unable to save verification data:', error);
    if (await handleSessionError(error, onLogoutRef.current)) return true;
    if (error?.writeOutcomeUnknown) {
      window.alert('The change may have been saved, but Airtable did not confirm the result. Refresh before trying again.');
      return true;
    }
    window.alert(error?.message || 'Unable to save verification data. Please try again.');
    return true;
  }, []);

  const beginSaving = () => {
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    return true;
  };

  const finishSaving = () => {
    savingRef.current = false;
    setSaving(false);
  };

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => () => {
    countRequestIdRef.current += 1;
  }, [elder.id, languageUuid]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError('');
        setLoadWarning('');
        setCanRetry(true);
        const {
          languageName: name,
          translations: data,
          verifiedTranslations: verified,
          verifiedTruncated,
          partial,
        } =
          await getLanguageVerificationData(elder.id, languageUuid);
        if (cancelled) return;

        const translationsNeedingVerification = data.filter(
          translation =>
            translation.translation &&
            !translation.elderVerified.includes(elder.id) &&
            !translation.elderSkipped.includes(elder.id)
        );

        const translationsNeedingTranslation = data.filter(
          translation => !translation.translation
        );

        const byPhrase = (a, b) => String(a?.phrase ?? '').localeCompare(String(b?.phrase ?? ''));
        translationsNeedingVerification.sort(byPhrase);
        translationsNeedingTranslation.sort(byPhrase);
        verified.sort(byPhrase);

        setTranslations({
          needingVerification: translationsNeedingVerification,
          needingTranslation: translationsNeedingTranslation
        });
        setCurrentTranslation(translationsNeedingVerification[0] || translationsNeedingTranslation[0] || null);
        setVerifiedTranslations(verified);
        setLanguageName(name);
        if (verifiedTruncated) {
          setLoadWarning('Showing the first 100 completed phrases.');
        } else if (partial) {
          setLoadWarning('Some language details or completed phrases are temporarily unavailable.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Unable to load verification data:', error);
        if (await handleSessionError(error, onLogoutRef.current)) return;
        if (NON_RETRYABLE_DATA_CODES.includes(error?.code)) {
          setCanRetry(false);
          setLoadError(error.code === 'LANGUAGE_NOT_ALLOWED'
            ? 'This language is not available for this Elder. Please switch languages or log in again.'
            : 'Verification data is not configured for this environment.');
        } else {
          setLoadError('Unable to load verification data. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [languageUuid, elder.id, loadAttempt]);

  useEffect(() => {
    if (currentTranslation) {
      setNewTranslation(currentTranslation.translation || '');
    }
  }, [currentTranslation]);

  const updateVerifiedCount = useCallback(async () => {
    if (readOnly) return;
    const requestId = ++countRequestIdRef.current;
    try {
      const { totalVerified, totalVerifiedToday } = await getElderVerificationCounts(elder.id);
      if (requestId !== countRequestIdRef.current) return;
      console.log('Updated verified count:', { totalVerified, totalVerifiedToday });
      setTotalVerified(totalVerified);
      setTodayVerified(totalVerifiedToday);
      onCountsLoaded();
    } catch (error) {
      if (requestId !== countRequestIdRef.current) return;
      console.error('Unable to refresh verification counts:', error);
      if (await handleSessionError(error, onLogoutRef.current)) return;
      onCountsError();
    }
  }, [elder.id, onCountsError, onCountsLoaded, readOnly, setTotalVerified, setTodayVerified]);

  const handleApproval = async (isApproved) => {
    if (readOnly) return;
    if (!isApproved) {
      if (!savingRef.current) setEditMode(true); // Set to edit mode when Wrong is clicked
      return;
    }
    if (!beginSaving()) return;
    try {
      await updateTranslation(currentTranslation.id, {
        'Elder Verified': [...(currentTranslation.elderVerified || []), elder.id]
      });
      removeCurrentTranslation();
      setIsRecording(true);
      updateVerifiedCount(); // Update verified count after removal

      // Update verified translations
      setVerifiedTranslations((prevVerified) => {
        const updatedVerified = [...prevVerified, currentTranslation];
        updatedVerified.sort((a, b) => a.phrase.localeCompare(b.phrase)); // Sort the dropdown alphabetically
        return updatedVerified;
      });
    } catch (error) {
      await handleWriteError(error);
    } finally {
      finishSaving();
    }
  };

  const handleSkip = async () => {
    if (readOnly || !beginSaving()) return;
    try {
      await updateTranslation(currentTranslation.id, {
        'Elder Skipped': [...(currentTranslation.elderSkipped || []), elder.id],
      });
      removeCurrentTranslation();
      updateVerifiedCount(); // Update verified count after removal
      moveToNextTranslation();
    } catch (error) {
      await handleWriteError(error);
    } finally {
      finishSaving();
    }
  };

  const handleRecordingSubmit = async (s3Url, { attachmentStored = false } = {}) => {
    if (readOnly || !beginSaving()) return;
    try {
      if (!attachmentStored) {
        await updateTranslation(currentTranslation.id, {
          'Spoken Sample': [
            {
              url: s3Url,
              filename: 'recording.webm'
            }
          ]
        });
      }
      setIsRecording(false);
      moveToNextTranslation();
    } catch (error) {
      await handleWriteError(error);
    } finally {
      finishSaving();
    }
  };

  const handleEditSubmit = async () => {
    if (readOnly) return;
    if (!newTranslation.trim()) {
      window.alert('Please enter a translation.');
      return;
    }
    if (!beginSaving()) return;
    try {
      await updateTranslation(currentTranslation.id, {
        'Translation': newTranslation,
        'Elder Verified': [...(currentTranslation.elderVerified || []), elder.id]
      });
      setEditMode(false);
      setIsRecording(true); // After edit, move to recording screen
      removeCurrentTranslation();
      updateVerifiedCount(); // Update verified count after removal

      // Update verified translations
      setVerifiedTranslations((prevVerified) => {
        const updatedVerified = [...prevVerified, currentTranslation];
        updatedVerified.sort((a, b) => a.phrase.localeCompare(b.phrase)); // Sort the dropdown alphabetically
        return updatedVerified;
      });
    } catch (error) {
      await handleWriteError(error);
    } finally {
      finishSaving();
    }
  };

  const handleNewTranslationSubmit = async () => {
    if (readOnly) return;
    if (!newTranslation.trim()) {
      window.alert('Please enter a translation.');
      return;
    }
    if (!beginSaving()) return;
    try {
      await updateTranslation(currentTranslation.id, {
        'Translation': newTranslation,
        'Elder Verified': [...(currentTranslation.elderVerified || []), elder.id]
      });
      setIsRecording(true); // Move to recording screen after saving the translation
      removeCurrentTranslation();
      updateVerifiedCount(); // Update verified count after removal

      // Update verified translations
      setVerifiedTranslations((prevVerified) => {
        const updatedVerified = [...prevVerified, currentTranslation];
        updatedVerified.sort((a, b) => a.phrase.localeCompare(b.phrase)); // Sort the dropdown alphabetically
        return updatedVerified;
      });
    } catch (error) {
      await handleWriteError(error);
    } finally {
      finishSaving();
    }
  };

  const removeCurrentTranslation = () => {
    setTranslations(prevTranslations => {
      const updatedVerification = prevTranslations.needingVerification.filter(t => t.id !== currentTranslation.id);
      const updatedTranslation = prevTranslations.needingTranslation.filter(t => t.id !== currentTranslation.id);
      return {
        needingVerification: updatedVerification,
        needingTranslation: updatedTranslation,
      };
    });
  };

  const moveToNextTranslation = () => {
    const allTranslations = [...translations.needingVerification, ...translations.needingTranslation];
    const nextIndex = allTranslations.indexOf(currentTranslation) + 1;
    setCurrentTranslation(allTranslations[nextIndex] || null);
  };

  const handlePhraseClick = (translation) => {
    if (savingRef.current) return;
    setCurrentTranslation(translation);
    setEditMode(false);
    setIsRecording(false);
    if (!translation.translation) {
      setNewTranslation(''); // Reset newTranslation if translation is empty
    }
  };

  const handleReviewPhraseClick = (translationId) => {
    if (savingRef.current) return;
    const translation = verifiedTranslations.find(t => t.id === translationId);
    if (translation) {
      setCurrentTranslation(translation);
      setIsRecording(false);
      setEditMode(false);
      if (!translation.translation) {
        setNewTranslation('');
      }
    }
  };

  const handleAddNewPhrase = async () => {
    if (readOnly || addingPhraseRef.current) return;
    if (!newPhrase.trim()) {
      window.alert('Please enter a word or phrase.');
      return;
    }
    addingPhraseRef.current = true;
    setAddingPhrase(true);
    try {
      if (await checkPhraseExists(newPhrase)) {
        window.alert('This phrase already exists.');
        return;
      }
      await addNewPhrase(newPhrase, newPhraseTranslation, elder.id, languageId);
      window.alert('New phrase added successfully.');
      setNewPhrase('');
      setNewPhraseTranslation('');
      setShowAddPhraseForm(false); // Hide the form after adding a new phrase
    } catch (error) {
      await handleWriteError(error);
    } finally {
      addingPhraseRef.current = false;
      setAddingPhrase(false);
    }
  };

  useEffect(() => {
    updateVerifiedCount();
  }, [updateVerifiedCount]);

  return (
    <div className="translation-verification">
      <div className="side-menu">
        {!readOnly && (
          <button disabled={loading || Boolean(loadError) || saving} onClick={() => setShowAddPhraseForm(prev => !prev)} className="header-button">+ Add Word/Phrase</button>
        )}
        {verifiedTranslations.length > 0 && (
          <>
            <h3>Review Completed Phrases</h3>
            <select disabled={saving} onChange={(e) => handleReviewPhraseClick(e.target.value)}>
              <option value="">Select a phrase</option>
              {verifiedTranslations.map(translation => (
                <option key={translation.id} value={translation.id}>
                  {capitalizeFirstLetter(translation.phrase)}
                </option>
              ))}
            </select>
          </>
        )}
        {translations.needingVerification.length > 0 && (
          <>
            <h3>Phrases Awaiting Confirmation</h3>
            <ul>
              {translations.needingVerification.map(translation => (
                <li
                  key={translation.id}
                  className={currentTranslation && currentTranslation.id === translation.id ? 'active' : ''}
                  onClick={() => handlePhraseClick(translation)}
                >
                  {capitalizeFirstLetter(translation.phrase)}
                </li>
              ))}
            </ul>
          </>
        )}
        {translations.needingTranslation.length > 0 && (
          <>
            <h3>Phrases Needing Translation</h3>
            <ul>
              {translations.needingTranslation.map(translation => (
                <li
                  key={translation.id}
                  className={currentTranslation && currentTranslation.id === translation.id ? 'active' : ''}
                  onClick={() => handlePhraseClick(translation)}
                >
                  {capitalizeFirstLetter(translation.phrase)}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <div className="main-content">
        {loadWarning && <p className="data-warning" role="status">{loadWarning}</p>}
        {loading ? (
          <p>Loading verification data…</p>
        ) : loadError ? (
          <div className="data-error" role="alert">
            <p>{loadError}</p>
            {canRetry && <button onClick={() => setLoadAttempt(attempt => attempt + 1)}>Retry</button>}
          </div>
        ) : !readOnly && showAddPhraseForm ? (
          <div className="edit-container">
            <h3>Add New Word/Phrase</h3>
            <input
              type="text"
              value={newPhrase}
              onChange={(e) => setNewPhrase(e.target.value)}
              placeholder="Enter new word/phrase"
            />
            <input
              type="text"
              value={newPhraseTranslation}
              onChange={(e) => setNewPhraseTranslation(e.target.value)}
              placeholder="Enter translation"
            />
            <button disabled={addingPhrase} onClick={handleAddNewPhrase}>
              {addingPhrase ? 'Adding…' : 'Add Phrase'}
            </button>
            <button disabled={addingPhrase} onClick={() => setShowAddPhraseForm(false)}>Cancel</button>
          </div>
        ) : currentTranslation ? (
          !readOnly && isRecording ? (
            <div>
              <div className="phrase">{capitalizeFirstLetter(newTranslation)}</div> {/* Show edited translation */}
              <CustomAudioRecorder
                onSubmit={handleRecordingSubmit}
                onError={handleWriteError}
                selectedMic={selectedMic}
                onMicChange={setSelectedMic}
                translationId={currentTranslation.id}
              />
            </div>
          ) : !readOnly && editMode ? ( // Show edit input if in edit mode
            <div className="edit-container">
              <div className="phrase">{capitalizeFirstLetter(currentTranslation.phrase)}</div> {/* Show English word above edit box */}
              <input
                type="text"
                value={newTranslation}
                onChange={(e) => setNewTranslation(e.target.value)}
                placeholder="Enter translation"
              />
              <button disabled={saving} onClick={handleEditSubmit}>{saving ? 'Saving…' : 'Submit'}</button>
            </div>
          ) : (
            <div>
              <div className="phrase-container">
                <div className="phrase-section">
                  <div className="phrase">{capitalizeFirstLetter(currentTranslation.phrase)}</div>
                  <h4 className="source-language">English</h4>
                </div>
                <div className="translation-section">
                  <div className="translation">{capitalizeFirstLetter(currentTranslation.translation)}</div>
                  <h4 className="source-language">{capitalizeFirstLetter(languageName)}</h4>
                </div>
              </div>
              {readOnly ? (
                <p className="read-only-message">
                  Read-only preview: verification, translation, and recording changes are disabled.
                </p>
              ) : currentTranslation.translation ? (
                <div className="buttons">
                  <button disabled={saving} className="correct" onClick={() => handleApproval(true)}>Correct</button>
                  <button disabled={saving} className="not-sure" onClick={handleSkip}>Not sure</button>
                  <button disabled={saving} className="wrong" onClick={() => handleApproval(false)}>Wrong</button>
                </div>
              ) : (
                <div className="edit-container">
                  <input
                    type="text"
                    value={newTranslation}
                    onChange={(e) => setNewTranslation(e.target.value)}
                    placeholder="Enter translation"
                  />
                  <button disabled={saving} onClick={handleNewTranslationSubmit}>{saving ? 'Saving…' : 'Submit'}</button>
                </div>
              )}
            </div>
          )
        ) : (
          <p>No translations available for verification.</p>
        )}
      </div>
    </div>
  );
};

export default TranslationVerification;
